import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Book,
  Briefcase,
  FileText,
  Box,
  Monitor,
  Activity,
  Award,
  Users,
  Calculator,
  PenTool,
  Layers,
  Ruler,
  Settings,
  ChevronDownCircle,
} from "lucide-react";
import { Link } from "react-router-dom";

// Curriculum Array
const curriculum = [
  { title: "Introduction to CAD Principles", icon: Book },
  { title: "2D Drafting Techniques", icon: FileText },
  { title: "3D Modeling Fundamentals", icon: Box },
  { title: "CAD Software Mastery", icon: Monitor },
  { title: "Design Simulation & Analysis", icon: Activity },
  { title: "Industry Design Standards", icon: Award },
  { title: "Collaborative Project Work", icon: Users },
  { title: "Portfolio & Final Presentation", icon: Calculator },
];

// Careers Array
const careers = [
  {
    title: "CAD Technician",
    description:
      "Specializes in creating detailed 2D and 3D drawings for engineering and manufacturing projects.",
    icon: FileText,
  },
  {
    title: "Design Engineer",
    description:
      "Develops innovative designs and solutions for products, ensuring functionality and efficiency.",
    icon: Monitor,
  },
  {
    title: "Draftsman",
    description:
      "Produces precise technical drawings and blueprints for engineers, architects, and builders.",
    icon: PenTool,
  },
  {
    title: "3D Modeler",
    description:
      "Creates realistic 3D models and visualizations used in design, architecture, and animation.",
    icon: Box,
  },
  {
    title: "Product Designer",
    description:
      "Designs functional and aesthetically appealing products balancing usability and production.",
    icon: Award,
  },
];

// Syllabus Array
const syllabus = [
  { title: "Introduction & CAD Basics", icon: Layers },
  { title: "2D Drafting Tools & Commands", icon: Ruler },
  { title: "3D Modeling Techniques", icon: Box },
  { title: "Assembly & Simulation", icon: Settings },
];

// FAQs
const faqs = [
  {
    question: "Is this CAD course suitable for beginners?",
    answer:
      "Yes, the course starts with fundamental concepts and gradually advances, so no prior CAD experience is required.",
  },
  {
    question: "Which CAD software will I learn?",
    answer:
      "You will get hands-on training with popular tools like AutoCAD, SolidWorks, and Fusion 360.",
  },
  {
    question: "Will there be real-world design projects?",
    answer:
      "Definitely! The course includes practical projects that simulate industry design challenges.",
  },
  {
    question: "Do you offer placement assistance for CAD roles?",
    answer:
      "Yes, we provide career guidance, resume building, and connect you with industry partners for job opportunities.",
  },
];

export default function Cad() {
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="overflow-hidden">
      {/* Animations & Custom CSS */}
      <style>{`
        @keyframes floatCard { 
          0%,100% { transform: translateY(0); } 
          50% { transform: translateY(-6px); } 
        }
        @keyframes pulseIcon { 
          0%,100% { transform: scale(1); opacity:1; } 
          50% { transform: scale(1.15); opacity:0.9; } 
        }
        @keyframes bounce { 
          to { transform: translateY(-8px); } 
        }
        @keyframes rotate { 
          100% { transform: rotate(360deg); } 
        }

        .card-hover {
          position: relative;
          overflow: hidden;
          border-radius: 15px;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .card-hover:hover {
          transform: translateY(-8px) scale(1.04);
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.25);
        }
        .card-hover::before {
          content: "";
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: conic-gradient(#4caf50, #81c784, #2e7d32, #4caf50);
          animation: rotate 6s linear infinite;
          opacity: 0;
          transition: opacity 0.4s ease;
          z-index: 0;
        }
        .card-hover:hover::before {
          opacity: 1;
        }
        .card-hover > * {
          position: relative;
          z-index: 1;
        }

        .icon-animate:hover {
          animation: bounce 0.6s ease infinite alternate;
          color: #2e7d32 !important;
        }
      `}</style>

      {/* Header */}
      <header
        className="py-3 shadow-sm"
        style={{ background: "#4caf50", color: "#fff" }}
      >
        <div className="container d-flex justify-content-between align-items-center">
          <h4 className="m-0 fw-bold">CAD Academy</h4>
          <nav>
            <a href="#curriculum" className="text-white me-3 fw-semibold">
              Curriculum
            </a>
            <a href="#syllabus" className="text-white me-3 fw-semibold">
              Syllabus
            </a>
            <a href="#careers" className="text-white me-3 fw-semibold">
              Careers
            </a>
            <a href="#faq" className="text-white fw-semibold">
              FAQ
            </a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="text-white text-center d-flex align-items-center hero-section"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(/assets/CAD.jpg)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: "60vh",
          padding: "60px 20px",
        }}
      >
        <div className="container">
          <h1 className="display-3 fw-bold mb-3" data-aos="fade-down">
            CAD Course
          </h1>
          <p className="lead mb-4 text-lg md:text-xl" data-aos="fade-up">
            Unlock your creativity and technical expertise through immersive CAD
            projects, practical industry scenarios, and mentorship from seasoned
            engineers.
          </p>

          <Link
            to="/contact"
            className="btn btn-lg"
            style={{
              background: "#4caf50",
              color: "#fff",
              borderRadius: "30px",
              padding: "12px 30px",
            }}
            data-aos="zoom-in"
          >
            🚀 Enroll Now
          </Link>
        </div>
      </section>

      {/* Curriculum Section */}
      <section id="curriculum" className="py-5 text-center">
        <div className="container">
          <h2
            style={{
              fontSize: "1.8rem",
              fontWeight: 600,
              marginBottom: "1.5rem",
              color: "#000",
              textAlign: "center",
            }}
            data-aos="fade-up"
          >
            What You’ll Learn
          </h2>
          <div className="row g-4 justify-content-center">
            {curriculum.map((item, idx) => (
              <div
                key={idx}
                className="col-md-4"
                data-aos="zoom-in"
                data-aos-delay={idx * 150}
              >
                <div className="card-hover p-4 bg-white shadow-sm d-flex flex-column align-items-center">
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      width: "70px",
                      height: "70px",
                      borderRadius: "50%",
                      background: "#4caf50",
                      marginBottom: "15px",
                      animation: "pulseIcon 3s infinite",
                    }}
                  >
                    <item.icon
                      size={32}
                      color="#fff"
                      className="icon-animate"
                    />
                  </div>
                  <h6 className="fw-semibold" style={{ color: "#000" }}>
                    {item.title}
                  </h6>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Syllabus Section */}
      <section
        id="syllabus"
        className="py-5 text-center"
        style={{ background: "#f5f5f5" }}
      >
        <div className="container">
          <h2
            style={{
              fontSize: "1.8rem",
              fontWeight: 600,
              marginBottom: "1.5rem",
              color: "#000",
              textAlign: "center",
            }}
            data-aos="fade-up"
          >
            Course Syllabus
          </h2>
          <div className="row g-4 justify-content-center">
            {syllabus.map((item, idx) => (
              <div
                key={idx}
                className="col-md-3"
                data-aos="fade-up"
                data-aos-delay={idx * 200}
              >
                <div className="card-hover p-4 bg-white shadow-sm d-flex flex-column align-items-center">
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      width: "70px",
                      height: "70px",
                      borderRadius: "50%",
                      background: "#e8f5e9",
                      marginBottom: "15px",
                    }}
                  >
                    <item.icon
                      size={36}
                      style={{ color: "#4caf50" }}
                      className="icon-animate"
                    />
                  </div>
                  <h6 className="fw-semibold" style={{ color: "#000" }}>
                    {item.title}
                  </h6>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Paths Section */}
      <section className="text-center py-5" style={{ background: "#e0f2f1" }}>
        <div className="container">
          <h2
            style={{
              fontSize: "1.8rem",
              fontWeight: 600,
              marginBottom: "1.5rem",
              color: "#000",
              textAlign: "center",
            }}
            data-aos="fade-up"
          >
            Career Paths After This Course
          </h2>
          <div className="row justify-content-center g-4">
            {careers.map((career, idx) => (
              <div
                key={idx}
                className="col-md-3"
                data-aos="zoom-in"
                data-aos-delay={idx * 100}
              >
                <div className="card-hover p-4 bg-white shadow-sm d-flex flex-column align-items-center">
                  {/* Icon inside a circular container */}
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      width: "70px",
                      height: "70px",
                      borderRadius: "50%",
                      background: "#e8f5e9",
                      marginBottom: "15px",
                    }}
                  >
                    <career.icon
                      size={36}
                      style={{ color: "#4caf50" }}
                      className="icon-animate"
                    />
                  </div>

                  {/* Title */}
                  <h5 style={{ color: "#000" }}>{career.title}</h5>

                  {/* Description */}
                  <p
                    style={{
                      fontSize: "0.9rem",
                      textAlign: "center",
                      color: "#000",
                    }}
                  >
                    {career.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="py-5 text-center"
        style={{ background: "#4caf50", color: "#fff" }}
      >
        <div className="container">
          <h4 className="fw-bold mb-3">Ready to Master CAD?</h4>
          <p
            className="mb-4"
            style={{ fontSize: "1rem", maxWidth: "600px", margin: "0 auto" }}
          >
            Take the next step in your design career. Enroll now and start
            building your skills with hands-on projects, expert guidance, and
            industry-relevant training.
          </p>
          <Link
            to="/contact"
            className="btn btn-light px-4 py-2 fw-semibold"
            style={{
              borderRadius: "30px",
              transition: "all 0.3s ease",
              boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
              textDecoration: "none",
              color: "#4caf50",
              display: "inline-block",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = "#fff";
              e.currentTarget.style.color = "#2e7d32";
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = "";
              e.currentTarget.style.color = "#4caf50";
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            🚀 Enroll Now
          </Link>
        </div>
      </footer>
    </div>
  );
}
